import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { useMyProbeAssociations } from "../../_lib/use-probe-associations";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const DemoCard = styled(SettingsCard)({
  border: `1px dashed ${EPOCUS_BORDER}`,
  backgroundColor: "transparent",
});

const EnvironmentTag = styled("span")({
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_TEXT_SECONDARY,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "3px 8px",
  borderRadius: 4,
});

const LabelRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

import {
  type SeedUndocumentedResult,
  useSeedUndocumentedStudies,
} from "./use-seed-undocumented-studies";

type SeedState =
  | { status: "idle" }
  | { status: "seeding" }
  | { status: "done"; result: SeedUndocumentedResult }
  | { status: "failed" };

const NO_PROBE_MESSAGE =
  "Link a probe above first — seeded studies are stamped with your probe serial so they reach your queue.";

const HelperText: FCwC = ({ children }) => (
  <Typography style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

const resultMessage = (result: SeedUndocumentedResult): string => {
  switch (result.outcome) {
    case "seeded": {
      const studies = `${result.count} ${
        result.count === 1 ? "study" : "studies"
      }`;
      return `Added ${studies} to To Document. Images arrive once the studies finish syncing.`;
    }
    case "unconfigured":
      return "No demo studies are configured for this environment.";
    case "unavailable":
      return "Local database not ready. Please try again.";
  }
};

const statusMessage = (state: SeedState): string | null => {
  switch (state.status) {
    case "done":
      return resultMessage(state.result);
    case "failed":
      return "Couldn't add the studies. Please try again.";
    default:
      return null;
  }
};

export const DemoDataCard: FC = () => {
  const { seed } = useSeedUndocumentedStudies();
  const { associations } = useMyProbeAssociations();
  const [restricted] = useFeatureFlag(
    featureFlags.epocus.serialRestrictedTriage,
  );
  const [state, setState] = useState<SeedState>({ status: "idle" });
  const mySerial = associations[0]?.deviceSerialNumber ?? null;
  const needsProbe = restricted && !mySerial;
  const seedSerial = restricted ? mySerial : null;

  const onSeed = async () => {
    if (needsProbe) return;
    setState({ status: "seeding" });
    try {
      setState({ status: "done", result: await seed(seedSerial) });
    } catch (error) {
      // Raw messages/stacks may carry PHI/PII — log only the error name.
      console.error("Failed to seed undocumented studies", {
        errorName: error instanceof Error ? error.name : typeof error,
      });
      setState({ status: "failed" });
    }
  };

  const message = statusMessage(state);

  return (
    <DemoCard>
      <LabelRow>
        <SettingsCardLabel>Demo data</SettingsCardLabel>
        <EnvironmentTag>Dev &amp; demo only</EnvironmentTag>
      </LabelRow>
      <HelperText>
        {seedSerial
          ? `Adds a few unclaimed studies to the To Document queue on the home page, stamped with your linked probe ${seedSerial} as though they had arrived from that probe.`
          : "Adds a few unclaimed studies to the To Document queue on the home page, carrying no probe serial, as though they had arrived from a scanner nobody has registered."}{" "}
        Each study reuses a demo scan already held on the server, so its images
        appear once the study finishes syncing.
      </HelperText>
      <Box>
        <Button
          variant="outlined"
          size="small"
          onClick={onSeed}
          disabled={state.status === "seeding" || needsProbe}
          style={{ color: EPOCUS_TEXT_SECONDARY, borderColor: EPOCUS_BORDER }}
          startIcon={
            state.status === "seeding" ? (
              <CircularProgress size={14} style={{ color: "inherit" }} />
            ) : undefined
          }
        >
          Seed undocumented studies
        </Button>
      </Box>
      {needsProbe && <HelperText>{NO_PROBE_MESSAGE}</HelperText>}
      {message && (
        <Typography
          role="status"
          style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
        >
          {message}
        </Typography>
      )}
    </DemoCard>
  );
};
